import { useState, type ReactNode } from 'react'
import { GAIT_LIST } from '@/domain/hs1'
import { useSimulation } from '@/sim/SimulationProvider'

/**
 * Kleinteile, die auf jeder Simulatorseite vorkommen.
 *
 * Sie stehen hier und nicht in der Komponentenbibliothek, weil sie
 * fachspezifisch sind: ein Regler, der seine Grenzen aus einer Tabelle des
 * Rechenkerns bezieht, gehört nicht in eine allgemeine Bibliothek.
 */

export interface SliderProps {
  label: ReactNode
  value: number
  min: number
  max: number
  step: number
  unit?: string
  /** Wie viele Nachkommastellen angezeigt werden. Ohne Angabe aus `step` abgeleitet. */
  decimals?: number
  disabled?: boolean
  onChange: (value: number) => void
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  unit = '',
  decimals,
  disabled,
  onChange,
}: SliderProps) {
  const places = decimals ?? (step >= 1 ? 0 : step >= 0.1 ? 1 : 2)
  return (
    <label className="slider">
      <span className="slider__label">{label}</span>
      <span className="slider__value">
        {value.toFixed(places)}
        {unit ? ` ${unit}` : ''}
      </span>
      <input
        className="slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  )
}

export interface MetricProps {
  label: string
  value: ReactNode
  note?: ReactNode
  /** Hebt den Wert hervor, wenn eine Grenze gerissen ist. */
  warn?: boolean
}

export function Metric({ label, value, note, warn }: MetricProps) {
  return (
    <div className="metric">
      <span className="metric__label">{label}</span>
      <span className={`metric__value${warn ? ' metric__value--warn' : ''}`}>{value}</span>
      {note && <span className="metric__note">{note}</span>}
    </div>
  )
}

/** Zahl mit fester Stellenzahl — für Tabellen, in denen Spalten fluchten sollen. */
export const num = (value: number, decimals = 1): string =>
  Number.isFinite(value) ? value.toFixed(decimals) : '—'

/** Prozentwert. */
export const pct = (value: number, decimals = 0): string =>
  Number.isFinite(value) ? `${(value * 100).toFixed(decimals)} %` : '—'

/**
 * Kopfzeile einer Simulatorseite.
 *
 * Sie steht ÜBER dem Inhalt und nicht darin: die Bühne nimmt die rechte Hälfte
 * ein, und eine Überschrift, die mit dem Inhalt scrollt, verschwindet als
 * Erstes.
 */
export function PageHead({
  eyebrow,
  title,
  lead,
  actions,
}: {
  /* Beides freiwillig. Eine Seite, deren Name schon alles sagt, bekommt keinen
     zweiten Satz dazu — ein Vorspann, der nur den Titel umschreibt, ist die
     Zeile, die man beim zweiten Besuch überliest. */
  eyebrow?: string
  title: string
  lead?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="page-head">
      <div className="page-head__text">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {lead && <p className="page-lead">{lead}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </header>
  )
}

/* -------------------------------------------------------------------------
   Zahleneingabe in einer Tabelle
   ------------------------------------------------------------------------- */

export interface NumberCellProps {
  value: number
  /** Der Wert im Grundstand — er steht im Hinweistext und macht das Zurücknehmen möglich. */
  base: number
  min: number
  max: number
  step: number
  unit?: string
  digits?: number
  /** `null` heisst: auf den Grundstand zurück. */
  onChange: (value: number | null) => void
  /** Nicht änderbar — etwa der Nullpunkt des Punktesatzes. */
  readOnly?: boolean
}

/**
 * Eine änderbare Zahl in einer Tabelle.
 *
 * SIE SIEHT WIE TEXT AUS, BIS MAN SIE ANFASST. Eine Tabelle voller gerahmter
 * Eingabefelder liest sich wie ein Formular; diese Tabellen sollen sich wie
 * Datenblätter lesen. Der Rahmen erscheint beim Überfahren, die Akzentkante bei
 * einem geänderten Wert.
 *
 * DER ENTWURF WIRD ERST BEIM VERLASSEN ÜBERNOMMEN. Wer „−8" tippt, tippt
 * unterwegs „−" und dann „−8"; jede Zwischenstufe sofort zu übernehmen hiesse,
 * die ganze Simulation auf einer halb eingegebenen Zahl zu rechnen. Escape
 * verwirft, Enter übernimmt.
 */
export function NumberCell({
  value,
  base,
  min,
  max,
  step,
  unit = '',
  digits = 0,
  onChange,
  readOnly,
}: NumberCellProps) {
  const [draft, setDraft] = useState<string | null>(null)
  const changed = Math.round(value * 10 ** digits) !== Math.round(base * 10 ** digits)

  if (readOnly) {
    return (
      <span className="cell-muted numeric">
        {value.toFixed(digits)}
        {unit ? ` ${unit}` : ''}
      </span>
    )
  }

  const commit = () => {
    if (draft === null) return
    const text = draft.trim().replace(',', '.')
    setDraft(null)
    if (text === '') return onChange(null)
    const parsed = Number(text)
    if (!Number.isFinite(parsed)) return
    onChange(Math.min(max, Math.max(min, parsed)))
  }

  return (
    <span className="numcell">
      <input
        className={`numcell__input${changed ? ' numcell__input--set' : ''}`}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={draft ?? value.toFixed(digits)}
        title={
          changed
            ? `Grundstand ${base.toFixed(digits)}${unit ? ` ${unit}` : ''} — Feld leeren setzt zurück`
            : `${min} … ${max}${unit ? ` ${unit}` : ''}`
        }
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
          if (event.key === 'Escape') {
            setDraft(null)
            event.currentTarget.blur()
          }
        }}
      />
      {unit && <span className="numcell__unit">{unit}</span>}
    </span>
  )
}

/**
 * Eine Tabellenzeile anklickbar machen — sie markiert dann ihre Achse im
 * Bühnenfenster.
 *
 * WOZU. Eine Antriebstabelle nennt eine Achse „Knie sagittal rechts" und
 * daneben 306 Nm. Wer die Maschine nicht im Kopf hat, weiss damit, WIE VIEL,
 * aber nicht WO. Ein Klick setzt einen Ring an genau dieses Gelenk.
 *
 * ALS HILFSFUNKTION UND NICHT ALS KOMPONENTE, weil eine Tabellenzeile keine
 * Komponente sein kann, ohne dass zwischen `<tbody>` und `<tr>` ein fremdes
 * Element steht. Die Funktion liefert die Eigenschaften, die eine Zeile
 * anklickbar machen — Klasse, Klick, und die Tastaturbedienung dazu.
 *
 * EIN ZWEITER KLICK HEBT AUF. Eine Markierung, die man nur über ein Kreuz im
 * Bild wieder loswird, ist eine Falle: man klickt auf die Zeile, um sie
 * loszuwerden, und markiert sie erneut.
 */
export function axisRow(
  focus: { joint: string; side: 'R' | 'L' | null } | null,
  setFocus: (next: { joint: string; side: 'R' | 'L' | null; label: string } | null) => void,
  axis: { joint: string; side: 'R' | 'L' | null; label: string },
) {
  const on = focus !== null && focus.joint === axis.joint && focus.side === axis.side
  const toggle = () => setFocus(on ? null : axis)
  return {
    className: `axis-row${on ? ' axis-row--on' : ''}`,
    onClick: toggle,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      toggle()
    },
    tabIndex: 0,
    role: 'button',
    'aria-pressed': on,
    title: on ? 'Markierung aufheben' : `${axis.label} im Bühnenfenster markieren`,
  }
}


/* -------------------------------------------------------------------------
   Gangartschalter
   ------------------------------------------------------------------------- */

/**
 * Die Bewegung umschalten, dort wo man die Figur sieht.
 *
 * WARUM NICHT NUR AUF DER PARAMETERSEITE. Freigang, Umhüllung, Balance und
 * Simulation zeigen alle dieselbe Figur — und die Frage, die man dort stellt,
 * ist fast immer „und im Sprint?". Wer dafür auf eine andere Seite wechseln,
 * dort umschalten und zurückgehen muss, vergleicht nicht mehr, sondern erinnert
 * sich. Der Schalter gehört deshalb an das Bühnenfenster.
 *
 * ER SCHREIBT IN DENSELBEN ZUSTAND wie die Parameterseite: es gibt eine
 * Bewegung und nicht eine je Seite. Wer hier umschaltet, findet die Umschaltung
 * dort wieder — und alle Zahlen ziehen mit.
 */
export function GaitBar() {
  const { state, setGait } = useSimulation()
  return (
    <div className="gaitbar" role="group" aria-label="Bewegung">
      {GAIT_LIST.map((entry) => (
        <button
          key={entry.key}
          type="button"
          className={entry.key === state.gait ? 'gaitbar__pick gaitbar__pick--on' : 'gaitbar__pick'}
          aria-pressed={entry.key === state.gait}
          title={entry.note}
          onClick={() => setGait(entry.key)}
        >
          {entry.label}
        </button>
      ))}
    </div>
  )
}
